/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent } from 'react';
import { FilledTextFieldProps, TextFieldProps, Autocomplete } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { TextField } from '../../../../../../../components/text-field';

const useStyles = makeStyles()(() =>
  ({
    paper: {
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
    },
  })
);

// Please think carefully before adding new props to this.
export interface IMultiSelectProps<T, ClassKey extends string = string> {
  classes?: Partial<Record<ClassKey, string>>;
  label: string;
  value: T[] | undefined;
  onChange: (value: T[]) => void;
  getOptionLabel: (option: T) => string;
  renderOption: (props: object, option: T, state: object) => React.ReactNode;
  options: T[];
  disabled?: boolean;
  disableClearable?: true;
  error?: boolean;
  errorText?: FilledTextFieldProps['helperText'];
  required?: boolean;
  className?: string;
  isOptionEqualToValue?: (option: T, val: T) => boolean;
  style?: React.CSSProperties;
  noOptionsText?: string;
  variant?: TextFieldProps['variant'];
  readonly?: boolean;
  inputRootClass?: string;
  inputElementRootClass?: string;
  size?: TextFieldProps['size'];
  placeholder?: TextFieldProps['placeholder'];
  title?: string;
}

export function MultiSelect<T>(props: IMultiSelectProps<T>) {
  const {
    label,
    required,
    onChange,
    value,
    error,
    errorText,
    renderOption,
    noOptionsText,
    variant,
    readonly,
    disabled,
    inputRootClass = '',
    inputElementRootClass = '',
    size,
    placeholder,
    title,
    ...rest
  } = props;

  function handleOnChange(event: ChangeEvent<{}>, selected: T[]) {
    onChange && onChange(selected);
  }

  const { classes } = useStyles();

  return (
    <Autocomplete<T, true, true>
      {...rest}
      multiple
      value={value || []}
      onChange={handleOnChange}
      classes={{
        paper: classes.paper,
        inputRoot: inputRootClass,
      }}
      ChipProps={{
        variant: 'outlined',
        size: 'small',
      }}
      autoHighlight
      selectOnFocus
      disableCloseOnSelect
      renderOption={renderOption}
      renderInput={params => {
        const { inputProps } = params;
        const { className: textFieldClassName } = inputProps;
        return (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            errorText={errorText}
            variant={variant}
            multiline={false}
            title={title}
            inputProps={{
              ...inputProps,
              className: `${textFieldClassName} ${inputElementRootClass}`,
              autoComplete: 'off',
              placeholder,
            }}
            disabled={readonly ? false : params.disabled}
            size={size}
          />
        );
      }}
      noOptionsText={noOptionsText || 'Ingen valgmuligheder'}
      disabled={disabled || readonly}
    />
  );
}
